import {TSyncfield} from "./index";
import {Permission} from './constants/Permission';
import {ImportFormatId} from "./constants/ImportFormats";
import {SyncstepId} from "./constants/Syncstep";
import {ObjectTypeId} from "./constants/ObjectTypeId";

export {SyncstepId} from "./constants/Syncstep";
export {ObjectTypeId} from "./constants/ObjectTypeId";

// Token key
// The variable name of the session cookie.
export const TOKEN_KEY = "__t";
// The variable name of the session super cookie.
export const TOKEN_KEY_SUPER = "__st";
// The variable name of the hash that is generated for a user (in order to recognize whether the user has logged in before or not).
export const USER_HASH_KEY = "__uh";
// The window name of this application.
export const WIN_NAME_APP = "ea_app";
// The window name of the documentation.
export const WIN_NAME_DOCS = "ea_docs";
// The window name used when demoing iframe content. I.e. department hierarchy.
export const WIN_NAME_IFRAME = "ea_iframe";

// Session duration
// The duration of the session cookie.
export const SESSION_DURATION =
    8 * // hrs
    60 * // min
    60 * // sec
    1000 // ms
;

// Ajax method
// Whether to use the POST or GET method when performing ajax requests.
export const AJAX_METHOD = "POST";

// Ajax timeout duration
// How long to wait before an ajax request times out.
export const AJAX_TIMEOUT_DURATION =
    2 * // min
    60 * // sec
    1000 // ms
;

// Max cache size
// The maximum size of the memory cache before it is flushed.
// Note that when calculating the cache size the cache is JSON-stringified. This doesn't give a precise byte value, but
// rather an approximation - which is good enough.
export const MAX_CACHE_SIZE =
    1024 * // bytes
    1024 * // KB
    5 // MB
;

// Cache duration
// The time in ms until a response is considered stale (and must be reloaded).
export const CACHE_DURATION_MEMORY =
    10 * // min
    60 * // sec
    1000 // ms
;

// Cache duration
// The time in ms until a response is considered stale (and must be reloaded).
export const CACHE_DURATION_PERSISTENT =
    8 * // hours
    60 * // min
    60 * // sec
    1000 // ms
;

// Request throttle
// The time in ms to wait before executing a request. Useful in real time search.
export const REQUEST_THROTTLE = 400;
export const REQUEST_THROTTLE_QUICK = 50;

// Delays in ms for repeating API requests.
export const CHECK_FOR_UPDATES_THROTTLE_DURATION = 5 * 1000;
export const CHECK_FOR_VERSION_THROTTLE_DURATION = 60 * 1000;
export const CHECK_FOR_SYNC_UPDATES_THROTTLE_DURATION = 10 * 1000;

// Available number of search results.
export const AVAILABLE_NUM_SEARCH_RESULTS = [10, 25, 50, 100] as const;
export const DOWNLOAD_THRESHOLD_MANY_OBJECTS = 500

// Default params for ajax search requests
export const RESULTS_PER_PAGE: typeof AVAILABLE_NUM_SEARCH_RESULTS[number] = 25;
export const DEFAULT_SEARCH_PAGE = 0;
export const DEFAULT_SORT_ORDER = true;

// The default time in ms to display obfuscated content.
export const OBFUSCATE_VISIBILITY_DURATION = 10 * 1000;

// The maximum allowed number of results per page (as enforced by the server API).
export const MAX_RESULTS_PER_PAGE = 30000;

// The maximum number of history items shown. This limit is implicit set by the server API (getobject > history[]).
export const MAX_HISTORY_ITEMS = 100;

// The maximum number of concurrent requests we fire towards the server.
export const MAX_CONCURRENT_REQUESTS = 10;

// Default moment-format for input type "date".
export const INPUT_DATE_FORMAT: string = 'YYYY-MM-DD';
// Default moment-format for input type "datetime".
export const INPUT_DATETIME_FORMAT: string = 'YYYY-MM-DDTHH:mm';

// The maximum length of description for message flow, sync templates, and system access.
export const MAX_DESCRIPTION_LENGTH = 4000;

export type TObjectType = 'User' | 'Department' | 'Group' | 'Function' | 'History' | 'Event' | 'System' | 'FormData' | 'Message' | 'Ruleset' | 'FunctionAlias' | 'ExternalContact' | 'FormFunction' | 'ApiUser' | 'QueryAlias' | 'FEStandard' | 'BEStandard' | 'LegalUnit'

export const ObjectTypeid2Key = {
    [ObjectTypeId.User]: 'User',
    [ObjectTypeId.Department]: 'Department',
    [ObjectTypeId.Group]: 'Group',
    [ObjectTypeId.Function]: 'Function',
    [ObjectTypeId.History]: 'History',
    [ObjectTypeId.Event]: 'Event',
    [ObjectTypeId.System]: 'System',
    [ObjectTypeId.FormData]: 'FormData',
    [ObjectTypeId.Message]: 'Message',
    [ObjectTypeId.Ruleset]: 'Ruleset',
    [ObjectTypeId.FunctionAlias]: 'FunctionAlias',
    [ObjectTypeId.ExternalContact]: 'ExternalContact',
    [ObjectTypeId.FormFunction]: 'FormFunction',
    [ObjectTypeId.ApiUser]: 'ApiUser',
    [ObjectTypeId.QueryAlias]: 'QueryAlias',
    [ObjectTypeId.FEStandard]: 'FEStandard',
    [ObjectTypeId.BEStandard]: 'BEStandard',
    [ObjectTypeId.LegalUnit]: 'LegalUnit',
} as const

// When administrating syncfields of certain object types the import format is set. This record represents that
// relation.
export const ObjectTypeId2ImportFormatId: Partial<Record<ObjectTypeId, ImportFormatId>> = {
    [ObjectTypeId.Function]: ImportFormatId.PAGA,
    [ObjectTypeId.History]: ImportFormatId.PAGA,
    [ObjectTypeId.Event]: ImportFormatId.PAGA,
    [ObjectTypeId.System]: ImportFormatId.PAGA,
    [ObjectTypeId.FormFunction]: ImportFormatId.PAGA,
    [ObjectTypeId.ApiUser]: ImportFormatId.Visma,
    [ObjectTypeId.QueryAlias]: ImportFormatId.PAGA,
    [ObjectTypeId.FEStandard]: ImportFormatId.Visma,
    [ObjectTypeId.LegalUnit]: ImportFormatId.PAGA,
}

export const DEFAULT_SEARCH_COLUMNS: Partial<Record<ObjectTypeId, TSyncfield[]>> = {
    [ObjectTypeId.User]: [
        'Id',
        'IdentityType',
        'EmployeeNumber',
        'GivenName',
        'Surname',
        'DisplayName',
        'Department',
        'AeduPositionOrgName',
        'AenrollmentOrgName',
        'UserName',
        'Upn',
    ],
    [ObjectTypeId.Department]: [
        'Id',
        'DepartmentNumber',
        'Department',
        'OrgUnitnr',
        'OrgUnitType',
        'EduSchoolType',
    ],
    [ObjectTypeId.LegalUnit]: [
        'Id',
        'Name',
        'SourceId',
        'ExtensionAttribute1',
        'ExtensionAttribute2',
        'ExtensionAttribute3',
        'ExtensionAttribute4',
        'ExtensionAttribute5',
        'ExtensionAttribute8',
        'ExtensionAttribute9',
    ],
    [ObjectTypeId.Group]: [
        'Id',
        'Name',
        'Description',
        'GroupType',
        'EduSchoolType',
        'Parent',
    ],
    [ObjectTypeId.FormData]: [
        'FormId',
        'FormName',
        'CreatedBy',
        'SourceId',
        'Created',
        'GivenName',
        'Surname',
    ],
    [ObjectTypeId.ExternalContact]: [
        'GivenName',
        'Surname',
        'Socialsecuritynumber',
        'SourceId',
    ],
    [ObjectTypeId.ApiUser]: [
        'Id',
        'GivenName',
        'SourceId',
    ],
}

// Snackbar durations
// Standard durations for snackbars.
export const SNACKBAR_DURATION_SHORT = 3000;
export const SNACKBAR_DURATION_MEDIUM = 8000;
export const SNACKBAR_DURATION_LONG = 15000;

// The limit of characters per single SMS message and absolute upper limit for long SMS messages (chunks of 153 characters x 2).
export const SMS_LIMIT = 160;
export const SMS_ABSOLUTE_LIMIT = 153 * 2;
export const DEFAULT_SMTP_SENDER = 'noreply@identum.no'
export const DEFAULT_SMS_SENDER = 'eAdm'

// Password complexity
// Whether or not to expect password to include at least one big letter, number, etc.
export const PASSWORD_COMPLEXITY = false;

// The ideal length when generating new password.
export const PASSWORD_IDEAL_LENGTH = 12;

// Permission levels
// Default values pr. server configuration.
export {Permission};

// the name of the system access for eAdm
export const SYSTEM_MEGA_EADM = 'mega.eadm.no';

export const TEXT_WRAPPER_START = '[';
export const TEXT_WRAPPER_END = ']';

export const EXCHANGE_VERSIONS: string[] = ["2016", "2013", "2010", "2007", "Remote"];


export const SyncstepsWithCustomerKey: SyncstepId[] = [
    SyncstepId.Export_P360,
    SyncstepId.Export_P3602,
    SyncstepId.Export_Acos,
    SyncstepId.Export_Agresso,
    SyncstepId.Export_Compilo,
    SyncstepId.Export_Dossier,
    SyncstepId.Export_P360_Arkiv,
    SyncstepId.Export_Azure,
    SyncstepId.Export_Visma,
    SyncstepId.Export_NetPower,
    SyncstepId.Export_SikriElements,
    SyncstepId.Export_KSFiks,
    SyncstepId.Export_Motivati,
    SyncstepId.Export_Helseplatform,
]

// The available CSV-delimiters
export const AVAILABLE_CSV_DELIMITERS = [';', ','] as const;
export const DEFAULT_CSV_DELIMITERS = AVAILABLE_CSV_DELIMITERS[0]


// DEPRECATED;moved to config.json
// Recaptcha site key
// Site key for eadm.no, used when communicating with the recaptcha webservice.
// @see https://developers.google.com/recaptcha/docs/verify
// export const RECAPTCHA_SITE_KEY = '6Ldsfy8sAAAAAMJG1R_uRqSFUDoZrLStedZGeR7a';

export const SPINNER = `<i class="far fa-spin fa-spinner fa-fw" aria-hidden="true"></i>`;

// The maximum number of latest objects to show in menu.
export const LATEST_OBJECTS_SHOW_LIMIT = 3;

// The maximum number of objects to store in total (across all organizations).
export const LATEST_OBJECTS_STORE_LIMIT = 100;

// Whether to include latest objects in the menu automatically when opening a details page.
export const LATEST_OBJECTS_AUTOMATIC = true;

// Panel default toggles
export const LAYOUT_LEFT_TOGGLE = true;
export const LAYOUT_RIGHT_TOGGLE = false;

export const HIERARCHY_ARROW = '⏵'

// Available total results (for searchobjecthistory)
export const AVAILABLE_MAX_NUM_SEARCH = [500, 1000, 2500, 5000, 10000, MAX_RESULTS_PER_PAGE] as const;

// Default password requirement
export const PASSWORD_MINIMUM_LOWERCASE_LETTERS = 1

export const EHUB_IDS = {
    css_id: 'css-ehub',
    script_id: 'script-ehub',
    ehub_id: 'vue-ehub',
}


export const SERVICE_PREFIX = "req_"
